import { differenceInCalendarMonths } from "date-fns";
import { creationMonthDate, shiftCreationMonth } from "@revelate/calc/required-pipeline-creation";
import { REQUIRED_CREATION_MONTH_WEIGHTS, type CreationPlanning } from "./creationPlanning";

/** Approved historical display extension only. The fit, trajectory, comparisons,
 * source contracts and every existing returned value remain untouched. */
export function extendRequiredReference<T extends { month: string; required: number; leadIn: boolean }>(
  rows: readonly T[], fit: { level: number; trend: number }, startMonth: string,
  seasonalFactors: readonly number[], visibleStart: string,
): { rows: readonly (T | { month: string; required: number; leadIn: true; gap: null })[]; limitation: string | null } {
  const origin = creationMonthDate(startMonth), first = creationMonthDate(visibleStart);
  if (!rows.length || !origin || !first || seasonalFactors.length !== 12
    || ![fit.level, fit.trend, ...seasonalFactors].every(Number.isFinite)
    || seasonalFactors.some(value => value < 0)) return { rows, limitation: "Earlier Required reference cannot be supported by the existing fit." };
  const prefix: { month: string; required: number; leadIn: true; gap: null }[] = [];
  for (let month = visibleStart; month < rows[0].month; month = shiftCreationMonth(month, 1)) {
    const offset = differenceInCalendarMonths(creationMonthDate(month)!, origin);
    const required = seasonalFactors[Number(month.slice(5)) - 1] * (fit.level + fit.trend * offset);
    // Never clamp an unsupported extrapolation to zero or refit the plan.
    if (offset > 0 || !Number.isFinite(required) || required < 0) return { rows, limitation: "Earlier Required reference would be unsupported or negative; existing plan values are unchanged." };
    prefix.push({ month, required, leadIn: true, gap: null });
  }
  return { rows: [...prefix, ...rows], limitation: null };
}

export function planningRequiredReference(planning: CreationPlanning) {
  if (planning.required.state !== "ready" || planning.profile.state !== "ready" || !planning.plan) return null;
  return extendRequiredReference(planning.comparison?.rows ?? planning.required.value.rows,
    planning.required.value, planning.plan.startMonth, REQUIRED_CREATION_MONTH_WEIGHTS,
    shiftCreationMonth(planning.currentMonth, -12));
}
